import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import {
  AlterarFabricanteDtoType,
  fabricanteBreadcrumbItems,
} from "data/fabricantes";

import { transformErrors } from "helpers/utils";
import { useEffect } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useParams } from "react-router-dom";
import { usePermissionModal } from "contexts/PermissionModalContext";

const EditarFabricante = () => {
  const { id } = useParams();
  const { getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();

  const { isLoading, isError, data, error } = useQuery<{
    data: AlterarFabricanteDtoType;
  }>({
    queryKey: ["fabricante", id],
    queryFn: () => axios.get(`/api/fabricante/${id}`),
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: ["fabricante", id],
      defaultMessage: "Você não possui permissão para visualizar este fabricante"
    })
  });

  const handleFormErrors = (error: any) => {
    // Tratando o erro de forma segura com tipagem
    const errorData = error.response?.data as { errors?: Record<string, any> } || {};
    const formattedErrors = transformErrors(errorData.errors || {});
    for (const [field, errorValue] of Object.entries(formattedErrors)) {
      form.setError(field as any, errorValue as any);
    }
  };

  const mutation = useMutation<
    any,
    AxiosError<any>,
    { id: string; data: AlterarFabricanteDtoType }
  >({
    mutationFn: ({ id, data }) => axios.put(`/api/fabricante/${id}`, data),
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar fabricante",
      handleFormErrors
    )
  });

  const fabricante = data?.data;

  const form = useForm<AlterarFabricanteDtoType>();

  useEffect(() => {
    if (fabricante) {
      console.log(fabricante);
      form.reset(fabricante);
    }
  }, [fabricante]);

  if (isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (isError) {
    return <div>Ocorreu um erro ao carregar o fabricante</div>;
  }

  if (!fabricante) {
    return <div>Fabricante não encontrado</div>;
  }

  const onSubmit: SubmitHandler<AlterarFabricanteDtoType> = (data) => {
    mutation.mutate({ data, id: id || "" });
  };

  if (mutation.isSuccess) {
    return <Navigate to={`/cadastros/fabricantes`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={fabricanteBreadcrumbItems(fabricante?.codigo)} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar um fabricante</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações do fabricante
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={() => form.reset()}
              >
                Descartar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Informações do fabricante</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={2}
                  xl={2}
                  className="mb-5"
                  controlId="codigo"
                >
                  <Form.Control disabled value={fabricante?.codigo} required />
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={8}
                  xl={10}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição do fabricante"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarFabricante;
